import { usePage } from "@inertiajs/react"
import { useCallback } from "react"

import { HUB_TOUR_SECTIONS } from "@/components/demo-tour/hub-sections"
import type { TourPage } from "@/components/demo-tour/sections"
import type { DemoTourState } from "@/components/demo-tour/tour-engine"
import TourEngine from "@/components/demo-tour/tour-engine"
import {
  businessDemoTourPath,
  businessInboxPath,
  businessLeadsPath,
  businessReportingPath,
} from "@/routes"

interface HubTourPageProps {
  current_account?: {
    uid?: string
    account_type?: string
    demo_mode?: boolean
    demo_tour_state?: DemoTourState | null
  }
  [key: string]: unknown
}

/** The Customer Hub walkthrough for demo business accounts (Inbox → a to-do
 *  in its conversation → Leads → Reporting). */
export default function CustomerHubTourController() {
  const page = usePage<HubTourPageProps>()
  const account = page.props.current_account
  const businessUid = account?.uid

  // demo_tour_state is only emitted for demo-mode business accounts, so its
  // presence (plus the account type) is the enablement gate.
  const enabled = Boolean(
    businessUid &&
      account?.account_type === "business" &&
      account?.demo_mode &&
      account?.demo_tour_state !== undefined &&
      account?.demo_tour_state !== null,
  )

  // Detail pages sit behind dynamic ids only reachable by clicking (an inbox
  // conversation, a lead row), so their navigable path is the nearest list
  // page; pageMatches stays strict, and the engine waits there for the
  // visitor to click through.
  const pathForPage = useCallback(
    (target: TourPage): string | null => {
      if (!businessUid) return null
      switch (target.kind) {
        case "business_inbox":
        case "business_inbox_conversation":
          return businessInboxPath(businessUid)
        case "business_leads":
        case "business_lead_detail":
          return businessLeadsPath(businessUid)
        case "business_reporting":
          return businessReportingPath(businessUid)
        default:
          return null
      }
    },
    [businessUid],
  )

  const pageMatches = useCallback(
    (target: TourPage, path: string): boolean => {
      if (!businessUid) return false
      const base = `/business/${businessUid}`
      switch (target.kind) {
        case "business_inbox":
          return path === `${base}/inbox`
        // A conversation and its focused long-brief view share this path.
        case "business_inbox_conversation":
          return new RegExp(`^${base}/inbox/[^/]+$`).test(path)
        // The "go to Reporting" hand-off step plays from the lead page the
        // previous section ends on, so leads matches list and detail alike.
        case "business_leads":
          return path === `${base}/leads` || path.startsWith(`${base}/leads/`)
        case "business_lead_detail":
          return new RegExp(`^${base}/leads/[^/]+$`).test(path)
        case "business_reporting":
          return path === `${base}/reporting`
        default:
          return false
      }
    },
    [businessUid],
  )

  const isAutoStartPath = useCallback(
    (path: string): boolean =>
      businessUid !== undefined && path === `/business/${businessUid}/inbox`,
    [businessUid],
  )

  if (!enabled || !businessUid) return null

  return (
    <TourEngine
      sections={HUB_TOUR_SECTIONS}
      savedState={account?.demo_tour_state}
      persistUrl={businessDemoTourPath(businessUid)}
      pathForPage={pathForPage}
      pageMatches={pageMatches}
      isAutoStartPath={isAutoStartPath}
      celebrateOnFinish
    />
  )
}
